import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  Box,
} from '@mui/material'

export interface BatchTablePreview {
  name: string
  variable: string
  description: string
  /** Subfolder of the source folder (Regenerate walks subfolders); empty at the top. */
  folder?: string
}

export interface BatchConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  tables: BatchTablePreview[]
  top: string
  filter: string
  weight: string
  caseFilter: string
  destinationFolder: string
  loading?: boolean
  extraInfo?: React.ReactNode
}

const SpecSummaryRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  value ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>{label}:</Typography>
      <Chip label={value} size="small" variant="outlined" sx={{ maxWidth: 400, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
    </Stack>
  ) : null
)

const BatchConfirmDialog: React.FC<BatchConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Batch Generation',
  tables,
  top,
  filter,
  weight,
  caseFilter,
  destinationFolder,
  loading = false,
  extraInfo,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
            Spec Settings
          </Typography>
          <Stack spacing={0.5}>
            <SpecSummaryRow label="Top" value={top} />
            <SpecSummaryRow label="Filter" value={filter} />
            <SpecSummaryRow label="Weight" value={weight} />
            <SpecSummaryRow label="Case Filter" value={caseFilter} />
            <SpecSummaryRow label="Save to" value={destinationFolder} />
          </Stack>
        </Box>

        {extraInfo}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
            Tables to Generate ({tables.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Table Name</TableCell>
                  {tables.some(x => x.folder) && <TableCell sx={{ fontWeight: 600 }}>Folder</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Variable / Source</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tables.map((t, i) => (
                  <TableRow key={`${t.folder || ''}/${t.name}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{t.name}</TableCell>
                    {tables.some(x => x.folder) && <TableCell>{t.folder || '—'}</TableCell>}
                    <TableCell>{t.variable}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.description || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>Cancel</Button>
      <Button
        variant="contained"
        color="info"
        onClick={onConfirm}
        disabled={loading || tables.length === 0}
      >
        {loading ? 'Generating...' : `Generate ${tables.length} Table${tables.length !== 1 ? 's' : ''}`}
      </Button>
    </DialogActions>
  </Dialog>
)

export default BatchConfirmDialog
